import { useState } from 'react'
import { Modal } from '@components/UI/Modal'
import { Button } from '@components/UI/Button'
import { operatorApi } from '@services/api'
import styles from './SendToClientModal.module.css'

interface SendToClientModalProps {
  isOpen: boolean
  onClose: () => void
  submissionId: number | null
  clientEmail: string
  clientPhone: string
  clientName: string
  onSuccess: (submissionId: number) => void
}

export function SendToClientModal({
  isOpen,
  onClose,
  submissionId,
  clientEmail,
  clientPhone,
  clientName,
  onSuccess
}: SendToClientModalProps) {
  const [contactType, setContactType] = useState<'email' | 'phone'>('email')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ sentTo: string } | null>(null)

  const maskEmail = (email: string) => {
    if (!email) return 'Non disponibile'
    const [user, domain] = email.split('@')
    if (!domain || user.length <= 2) return email
    return `${user[0]}${'*'.repeat(user.length - 2)}${user[user.length - 1]}@${domain}`
  }

  const maskPhone = (phone: string) => {
    if (!phone) return 'Non disponibile'
    if (phone.length <= 4) return phone
    return `${'*'.repeat(phone.length - 4)}${phone.slice(-4)}`
  }

  const handleSend = async () => {
    if (!submissionId) {
      setError('ID pratica non disponibile')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const result = await operatorApi.sendToClient(submissionId, contactType)

      setSuccess({ sentTo: result.sent_to })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Errore durante l\'invio'
      setError(message)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    if (success && submissionId) {
      onSuccess(submissionId)
    }
    setSuccess(null)
    setError(null)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Invia al Cliente" size="md">
      <div className={styles.content}>
        {!success ? (
          <>
            {/* Client Info */}
            <div className={styles.clientInfo}>
              <i className="bi bi-person-circle" />
              <div>
                <strong>{clientName}</strong>
                <span>Riceverà il link per completare la firma</span>
              </div>
            </div>

            {/* Contact Method Selection */}
            <div className={styles.contactSection}>
              <label className={styles.label}>Come vuoi inviare il link?</label>
              
              <div className={styles.contactOptions}>
                <button
                  type="button"
                  className={`${styles.contactOption} ${contactType === 'email' ? styles.contactOptionActive : ''}`}
                  onClick={() => setContactType('email')}
                  disabled={!clientEmail}
                >
                  <div className={styles.contactIcon}>
                    <i className="bi bi-envelope" />
                  </div>
                  <div className={styles.contactDetails}>
                    <span className={styles.contactName}>Email</span>
                    <span className={styles.contactValue}>
                      {clientEmail ? maskEmail(clientEmail) : 'Non disponibile'}
                    </span>
                  </div>
                  {contactType === 'email' && clientEmail && (
                    <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-primary-500)' }} />
                  )}
                </button>

                <button
                  type="button"
                  className={`${styles.contactOption} ${contactType === 'phone' ? styles.contactOptionActive : ''}`}
                  onClick={() => setContactType('phone')}
                  disabled={!clientPhone}
                >
                  <div className={styles.contactIcon}>
                    <i className="bi bi-phone" />
                  </div>
                  <div className={styles.contactDetails}>
                    <span className={styles.contactName}>SMS</span>
                    <span className={styles.contactValue}>
                      {clientPhone ? maskPhone(clientPhone) : 'Non disponibile'}
                    </span>
                  </div>
                  {contactType === 'phone' && clientPhone && (
                    <i className="bi bi-check-circle-fill" style={{ color: 'var(--color-primary-500)' }} />
                  )}
                </button>
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className={styles.errorBanner}>
                <i className="bi bi-exclamation-triangle" />
                <span>{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className={styles.actions}>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isLoading}
              >
                Annulla
              </Button>
              <Button
                type="button"
                icon="send"
                iconPosition="right"
                onClick={handleSend}
                loading={isLoading}
                disabled={!clientEmail && !clientPhone}
              >
                Invia Link
              </Button>
            </div>
          </>
        ) : (
          /* Success State */
          <div className={styles.successState}>
            <div className={styles.successIcon}>
              <i className="bi bi-check-circle-fill" />
            </div>
            <h3>Invio completato</h3>
            <p>
              Il cliente {clientName} riceverà l&apos;invito per firmare il contratto
              {contactType === 'email' ? ' via email' : ' via SMS'} all&apos;indirizzo indicato in anagrafica.
              Il link non viene mostrato qui per sicurezza.
            </p>

            <div className={styles.sentInfo}>
              <i className="bi bi-check" />
              Inviato a: {success.sentTo}
            </div>

            <div className={styles.actions}>
              <Button
                type="button"
                onClick={handleClose}
              >
                Vai allo Stato Pratica
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
